'use client';

import Tooltip from '@/components/base/floating/tooltip/tooltip';
import { useSiteId } from '@/contexts/domain/hooks';
import { useFooter } from '@/features/footer/api/query/use-footer';
import { useBreakpointUp } from '@/hooks/use-theme';
import { Redirect, getRedirectPath } from '@/utils/redirects';
import { truncateText } from '@/utils/truncate-text';
import { MapPin, Phone } from '@phosphor-icons/react';
import { clsx } from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { memo } from 'react';
import { LanguageChange } from './language-change';
import { TextWithLeadingIcon } from './text-with-leading-icon';

interface Props {
  className?: string;
}

const InformationSection = ({ className }: Props) => {
  const locale = useLocale();
  const t = useTranslations('navigation');
  const isDesktop = useBreakpointUp('m');
  const isBreakpointM = useBreakpointUp('m');
  const siteId = useSiteId();

  const { data } = useFooter();

  const shouldTruncateText =
    isBreakpointM && (siteId === 'kg_ee' || siteId === 'ds_lv');

  const phoneNumberTranslation = t('information_section.phone_number');
  const ourStoresRedirect = getRedirectPath(Redirect.OUR_STORES, locale);
  const aboutUsRedirect = getRedirectPath(Redirect.ABOUT_US, locale);

  const phoneNumber =
    data?.contactInfo?.phones?.[0]?.title ?? phoneNumberTranslation;
  const hoursRange = data?.contactInfo?.openingHours?.[0]?.range;
  const hours = data?.contactInfo?.openingHours?.[0]?.hours;

  const openingHours = `${hoursRange}: ${hours}`;

  const { isTruncated, truncatedText, originalText } = truncateText(
    openingHours,
    20,
    shouldTruncateText,
  );

  return (
    <div
      className={clsx(
        'm:block h-full m:h-clickable-minimum m:max-h-clickable-minimum w-full s:items-start border-decorative border-b-1 bg-alt m:p-0 py-xxs s:py-m xs:py-xs',
        className,
      )}
    >
      <div className="container flex h-full m:flex-row flex-col m:justify-between">
        <div className="flex m:flex-row flex-col s:items-start items-center m:items-center">
          <div className="flex h-clickable-minimum items-center justify-center">
            <TextWithLeadingIcon
              icon={Phone}
              redirect={`tel:${phoneNumber}`}
              text={phoneNumber}
              aria-label={t('information_section.phone_number_title')}
              aria-description={phoneNumber}
              externalRoute
            />
            {/* //TODO: Uncomment when chat will be implemented */}
            {/* <span className="text-body px-xs text-default  m:text-small m:px-xxs">
              {t('information_section.or')}
            </span>
            <TextWithLeadingIcon
              icon={ChatCenteredText}
              // TODO: Should it redirect to /chat or open a chat?
              redirect="#"
              text={t('chat')}
            /> */}
          </div>
          {data?.contactInfo?.openingHours && (
            <>
              <div className="mx-xs m:block hidden h-s w-px bg-decorative" />
              <Tooltip
                enabled={isTruncated}
                placement="right"
                Content={<span className="text-small">{originalText}</span>}
                aria-description={originalText}
                className="flex h-full items-center"
              >
                <span
                  aria-description={originalText}
                  aria-label={t('information_section.opening_hours_title')}
                  tabIndex={0}
                  className={clsx(
                    'm:flex m:h-full m:items-center text-center m:text-default m:text-small text-body text-muted',
                    isTruncated && 'transition-all hover:text-strong',
                  )}
                >
                  {truncatedText}
                </span>
              </Tooltip>
            </>
          )}
        </div>
        <div className="m:mt-0 mt-xs flex h-clickable-minimum s:justify-start justify-center gap-l">
          <div className="flex flex-wrap justify-center gap-l">
            <TextWithLeadingIcon
              icon={MapPin}
              redirect={ourStoresRedirect}
              text={t('information_section.our_stores')}
              aria-label={t('information_section.our_stores')}
            />
            <TextWithLeadingIcon
              redirect={aboutUsRedirect}
              text={t('information_section.about_us')}
              aria-label={t('information_section.about_us')}
            />
          </div>
          {isDesktop && <LanguageChange />}
        </div>
      </div>
    </div>
  );
};
export default memo(InformationSection);
